import useChangeIssueTypeAffectedFields from './useChangeIssueTypeAffectedFields';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import SelectBox from '@adsk/bim360-matrix-react-select-box';
import {StatusColorIndicatorOption} from '@adsk/bim360-matrix-react-status-color-indicator';
import Text from '@adsk/bim360-matrix-react-text';
import {STATUS_OPEN, statusColorMapping, statusesByStatusSets, toStatusOption} from '../../constants/status-types';
import {Field, getIn, useFormikContext} from 'formik';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import i18n from '../../utils/i18n';
import IssueCustomAttributeField from './IssueCustomAttributeField';
import PropTypes from 'prop-types';
import {issueAttributesType} from '../../constants/proptype-validation';
import FormField from '../common/FormField';
import FormLabel from '../common/FormLabel';
import {getIssueCustomAttributeInputValidationMessage} from '../../utils/validation-helpers';
import {ISSUE_ATTRIBUTES_DATA_TYPES} from '../../constants/issue-attributes';

const ChangeIssueTypeModalContent = ({
  issueSubtypeId,
  issue,
  issueTypeEntitiesMap,
  issueCustomAttributeMappings,
  issueCustomAttributes
}) => {
  const pendingIssueSubtype = issueTypeEntitiesMap[issueSubtypeId];
  const {
    getStatusSet,
    doesNewTypeIncludeCurrentStatus,
    getIssueCustomAttributesThatWillBeLost,
    getNewCustomAttributeMappings
  } = useChangeIssueTypeAffectedFields({
    fieldIssueTypeEntitiesMap: issueTypeEntitiesMap,
    issueCustomAttributeMappings,
    issue
  });

  const newCustomAttributeMappings = useMemo(() => getNewCustomAttributeMappings(issueSubtypeId), [
    getNewCustomAttributeMappings,
    issueSubtypeId
  ]);

  const ref = useRef(null);

  const statusValueRenderer = useCallback(
    option =>
      option !== null && (
        <StatusColorIndicatorOption statusColor={statusColorMapping[option.value]} label={option.label} />
      ),
    []
  );

  const shouldShowNewStatusSetSection = !doesNewTypeIncludeCurrentStatus(pendingIssueSubtype.parentId);
  const issueCustomAttributesThatWillBeLost = useMemo(() => getIssueCustomAttributesThatWillBeLost(issueSubtypeId), [
    getIssueCustomAttributesThatWillBeLost,
    issueSubtypeId
  ]);

  const {errors, touched, values, setFieldTouched, setFieldValue} = useFormikContext();

  useEffect(() => {
    if (shouldShowNewStatusSetSection) {
      setFieldValue(ISSUE_ATTRIBUTE_NAMES.STATUS, STATUS_OPEN, false);
    }
  }, [setFieldValue, shouldShowNewStatusSetSection]);

  const statusOptions = useMemo(
    () =>
      shouldShowNewStatusSetSection
        ? statusesByStatusSets[getStatusSet(pendingIssueSubtype.parentId)].map(toStatusOption)
        : [],
    [getStatusSet, pendingIssueSubtype.parentId, shouldShowNewStatusSetSection]
  );
  return (
    <div ref={ref} className="ChangeIssueTypeModalContent">
      {shouldShowNewStatusSetSection && (
        <div className="ChangeIssueTypeModalContent__new-status">
          <Text hasHTML className="ChangeIssueTypeModalContent__new-status-description">
            {i18n.t('preview.changeTypeModal.newStatus', {
              issueSubtypeTitle: pendingIssueSubtype.title,
              currentStatus: i18n.t(`status.${issue.status}`),
              pendingStatus: i18n.t(`status.${STATUS_OPEN}`)
            })}
          </Text>
          <FormField labelText={i18n.t('new.status')}>
            <SelectBox
              className="ChangeIssueTypeModal__status"
              disabled
              options={statusOptions}
              value={statusOptions.find(({value}) => value === values[ISSUE_ATTRIBUTE_NAMES.STATUS])}
              valueRenderer={statusValueRenderer}
            />
          </FormField>
        </div>
      )}
      {newCustomAttributeMappings.length > 0 && (
        <div className="ChangeIssueTypeModalContent__new-attrs">
          <Text hasHTML containerClassName="ChangeIssueTypeModalContent__new-required-attrs-description">
            {i18n.t('preview.changeTypeModal.newRequiredCustomFieldsDescription', {
              issueSubtypeTitle: pendingIssueSubtype.title
            })}
          </Text>
          {newCustomAttributeMappings.map(({attributeDefinitionId, isRequired}) => {
            const customAttribute = issueCustomAttributes[attributeDefinitionId];
            const path = `${ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES}.${customAttribute.id}`;
            return (
              <Field
                name={path}
                validate={value => {
                  if (isRequired && !value) {
                    return i18n.t('new.fieldRequired');
                  }
                  return getIssueCustomAttributeInputValidationMessage(customAttribute.dataType, value);
                }}
                key={customAttribute.id}
              >
                {() => (
                  <IssueCustomAttributeField
                    isRequired={isRequired}
                    customAttribute={customAttribute}
                    value={getIn(values, path)}
                    onChange={v => {
                      setFieldTouched(path, true);
                      setFieldValue(path, v);
                    }}
                    showFieldError={getIn(touched, path) && !!getIn(errors, path)}
                    errorMessage={getIn(errors, path)}
                  />
                )}
              </Field>
            );
          })}
        </div>
      )}
      {issueCustomAttributesThatWillBeLost.length > 0 && (
        <div className="ChangeIssueTypeModalContent__lost-attrs">
          <Text hasHTML containerClassName="ChangeIssueTypeModalContent__lost-attrs-description">
            {i18n.t('preview.changeTypeModal.lostCustomAttributes', {
              issueSubtypeTitle: pendingIssueSubtype.title
            })}
          </Text>
          <ul className="ChangeIssueTypeModalContent__lost-attrs-list">
            {issueCustomAttributesThatWillBeLost.map(attribute => {
              const attributeDefinition = issueCustomAttributes[attribute.attributeDefinitionId];
              const dataType = attributeDefinition.dataType;
              const displayValue =
                dataType === ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST
                  ? attributeDefinition.metadata.list.options.find(option => option.id === attribute.value)?.value
                  : attribute.value;
              return (
                <li key={attribute.attributeDefinitionId}>
                  <FormLabel>{attribute.title}</FormLabel>
                  <div className="ChangeIssueTypeModalContent__lost-attrs-list-item-value">{displayValue}</div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

ChangeIssueTypeModalContent.displayName = 'ChangeIssueTypeModalContent';

ChangeIssueTypeModalContent.propTypes = {
  issueSubtypeId: PropTypes.string.isRequired,
  issueTypeEntitiesMap: PropTypes.object.isRequired,
  issueCustomAttributes: PropTypes.object.isRequired,
  issueCustomAttributeMappings: PropTypes.object.isRequired,
  issue: issueAttributesType.isRequired
};

export default ChangeIssueTypeModalContent;
